import { runtimeConfig } from "config/runtimeConfig";
import React, { useContext, useState } from 'react';
import { AppContext } from 'Context/AppContext';
import { useModule } from '../../contexts/ModuleContext';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import { ModelList } from '../../pages/ModelList/ModelList';
import EditorLayout from '../EditorLayout/EditorLayout';
import CreateUIModel from '../PopUp/CreateUIModel';
import { TParty } from '../../../../../../types/Context/AppContext';

type MainLayoutProps = {
  id?: string;
  projectId: string;
  userProjectDataModels: any;
  selectedParty: TParty;
  refreshRef?: React.MutableRefObject<(() => void) | null>;
  onFreestyleUIModelChange?: (hasFreestyle: boolean) => void;
};

export const MainLayout = ({
  id,
  projectId,
  userProjectDataModels,
  selectedParty,
  refreshRef,
  onFreestyleUIModelChange,
}: MainLayoutProps) => {
  const baseId = id || 'main-layout-1';
  const appData = useContext<any>(AppContext);
  const { activeView, setActiveView, hasEditPermission } = useModule();
  const { state } = useUIModelState();
  const { saveAll, isSaving, hasPendingSaves } = useSaveQueue();
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [isFlushing, setIsFlushing] = useState(false);

  const handleBackClick = () => {
    setActiveView('list');
  };

  const handleEditClick = () => {
    setShowEditPopup(true);
  };

  const handlePreviewClick = async () => {
    // Flush all pending saves before opening preview to ensure API has latest data
    if (hasPendingSaves || isSaving) {
      setIsFlushing(true);
      try {
        await saveAll();
      } catch (err) {
        console.error('Failed to flush saves before preview:', err);
      } finally {
        setIsFlushing(false);
      }
    }

    const selectedPartyName = (selectedParty!.name.replace(/ /g, '_')).toLowerCase();
    const rootPreviewURL: string = runtimeConfig.ROOT_PREVIEW_URL ?? '';
    const previewUrl: string = `https://${selectedPartyName}.${rootPreviewURL}/preview?modelId=${projectId}&uiModelId=${state.model?.id}&industry=${userProjectDataModels.industry}${appData.channelName ? `&env=${encodeURIComponent(appData.channelName.toLowerCase())}` : ''}`;
    window.open(previewUrl, '_blank')!.focus();
  };

  return activeView === 'list' ? (
    <ModelList
      id={`${baseId}-list`}
      projectId={projectId}
      userProjectDataModels={userProjectDataModels}
      selectedParty={selectedParty}
      refreshRef={refreshRef}
      onFreestyleUIModelChange={onFreestyleUIModelChange}
    />
  ) : (
    <>
      <EditorLayout
        id={`${baseId}-editor`}
        projectId={projectId}
        onBackClick={handleBackClick}
        onEditClick={handleEditClick}
        onPreviewClick={handlePreviewClick}
        hasEditPermission={hasEditPermission()}
      />
      {showEditPopup && (
        <CreateUIModel
          title="Update UI Model"
          projectId={projectId}
          uiModel={state.model}
          onSuccess={() => setShowEditPopup(false)}
          onClose={() => setShowEditPopup(false)}
        />
      )}
    </>
  );
};

export default MainLayout;
